import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Beer, Calendar, Clock, Users, AlertCircle, Loader, Building2 } from 'lucide-react';
import { supabase, handleSupabaseError, checkVenueOwnership } from '../lib/supabase';
import DateInput from '../components/DateInput';

const CreateDrinkingNight = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [venue, setVenue] = useState<any>(null);
  const [venueType, setVenueType] = useState<'restaurant' | 'bar' | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    spots_total: 2,
    price_range: '',
    age_range: '',
    preferred_gender: '',
    style: '',
    description: ''
  });

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Check venue ownership
      const { hasVenue, venue: userVenue, venueType: userVenueType } = await checkVenueOwnership(user.id);

      if (!hasVenue) {
        navigate('/restaurant-signup');
        return;
      }

      // Only allow bar owners to create drinking nights
      if (userVenueType !== 'bar') {
        navigate('/restaurant-signup');
        return;
      }

      setVenue(userVenue);
      setVenueType(userVenueType);
    } catch (err) {
      console.error('Error initializing component:', err);
      setError(handleSupabaseError(err).message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({
      ...prev,
      date
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!venue || venueType !== 'bar') return;

    try {
      setSubmitting(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('You must be logged in to create a drinking night');

      // Create the drinking night
      const { data: drinkingNight, error: insertError } = await supabase
        .from('drinking_nights')
        .insert({
          host_id: user.id,
          bar_id: venue.id,
          date: formData.date,
          time: formData.time,
          spots_total: parseInt(formData.spots_total.toString()),
          spots_available: parseInt(formData.spots_total.toString()),
          price_range: formData.price_range,
          age_range: formData.age_range,
          preferred_gender: formData.preferred_gender,
          style: formData.style,
          description: formData.description,
          status: 'open'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Navigate to the events page
      navigate('/my-events');
    } catch (err) {
      console.error('Error creating drinking night:', err);
      setError(handleSupabaseError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  if (!venue || venueType !== 'bar') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Bar Required</h2>
          <p className="text-gray-600 mb-4">
            You need to register a bar to create drinking nights.
          </p>
          <button
            onClick={() => navigate('/restaurant-signup')}
            className="bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition"
          >
            Register Your Bar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center space-x-2 mb-6">
          <Beer className="h-6 w-6 text-orange-500" />
          <h2 className="text-2xl font-bold text-gray-900">
            Veranstalte einen Barabend bei {venue.name}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <DateInput
                label="Datum"
                value={formData.date}
                onChange={handleDateChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>Uhrzeit</span>
                </div>
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Gruppengröße</span>
                </div>
              </label>
              <input
                type="number"
                name="spots_total"
                value={formData.spots_total}
                onChange={handleInputChange}
                min="2"
                max="8"
                className="w-full p-2 border rounded-md"
                placeholder="Anzahl an Personen"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preisklasse
              </label>
              <select
                name="price_range"
                value={formData.price_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Wähle Preisklasse</option>
                <option value="$">€ (Preiswert)</option>
                <option value="$$">€€ (Moderat)</option>
                <option value="$$$">€€€ (Gehoben)</option>
                <option value="$$$$">€€€€ (Luxuriös)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alter der Teilnehmer
              </label>
              <select
                name="age_range"
                value={formData.age_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Altersklasse wählen</option>
                <option value="21-25">21-25</option>
                <option value="26-35">26-35</option>
                <option value="36-45">36-45</option>
                <option value="46-55">46-55</option>
                <option value="56+">56+</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bevorzugtes Geschlecht
              </label>
              <select
                name="preferred_gender"
                value={formData.preferred_gender}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Geschlecht wählen</option>
                <option value="any">Egal</option>
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
                <option value="mixed">Gemischte Gruppe</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stil
              </label>
              <select
                name="style"
                value={formData.style}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Stil wählen</option>
                <option value="casual">Casual</option>
                <option value="upscale">Gehoben</option>
                <option value="sports">Sportlich</option>
                <option value="party">Party</option>
                <option value="social">Sozial</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md"
              rows={4}
              placeholder="Erzähle zukünftigen Gästen über den Abend..."
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full bg-orange-500 text-white px-6 py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center space-x-2 ${
              submitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {submitting ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                <span>Erstelle Event...</span>
              </>
            ) : (
              <>
                <Beer className="h-5 w-5" />
                <span>Erstelle Event</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateDrinkingNight;